import { isRecord } from './typeGuards';

// ============================================================================
// 错误分类类型定义
// ============================================================================

/**
 * 错误分类结果：用于区分不同类型的 API 错误并提取关键信息
 */
export type ErrorClassification =
  | { type: 'quota_insufficient'; userQuota: number; needQuota: number; rawMessage: string }
  | { type: 'rate_limited'; rawMessage: string }
  | { type: 'unauthorized'; rawMessage: string }
  | { type: 'server_error'; status: number; rawMessage: string }
  | { type: 'network_error'; rawMessage: string }
  | { type: 'unknown'; rawMessage: string };

/**
 * 额度详情：从错误消息中提取的配额信息
 */
export interface QuotaDetails {
  userQuota?: number;
  needQuota?: number;
}

// ============================================================================
// 错误识别函数
// ============================================================================

/**
 * 检测错误是否为额度不足类型
 * 识别模式：
 * - error.code 包含 'insufficient_quota'
 * - error.message 包含 'quota' 相关关键词
 */
export const isQuotaInsufficientError = (error: unknown): boolean => {
  if (error == null) return false;

  // 递归搜索 error 对象中的关键字段
  const searchInObject = (obj: unknown, depth = 0): boolean => {
    if (depth > 5 || obj == null) return false;

    if (typeof obj === 'string') {
      const lower = obj.toLowerCase();
      // 英文配额错误识别
      const isEnglishQuotaError =
        lower.includes('insufficient_quota') ||
        lower.includes('quota failed') ||
        lower.includes('quota exceeded') ||
        (lower.includes('quota') && lower.includes('insufficient'));
      if (isEnglishQuotaError) return true;

      // 中文配额/余额不足错误识别
      const isChineseQuotaError =
        obj.includes('配额不足') ||
        obj.includes('余额不足') ||
        obj.includes('额度不足') ||
        obj.includes('账户余额') ||
        obj.includes('账号余额');
      if (isChineseQuotaError) return true;

      return false;
    }

    if (!isRecord(obj)) return false;

    // 检查 code 字段
    const code = obj.code;
    if (typeof code === 'string') {
      const codeLower = code.toLowerCase();
      if (codeLower.includes('insufficient_quota') || codeLower.includes('quota_exceeded')) {
        return true;
      }
    }

    // 检查 type 字段
    const type = obj.type;
    if (typeof type === 'string' && type.toLowerCase().includes('quota')) {
      return true;
    }

    // 检查 message 字段
    const message = obj.message;
    if (typeof message === 'string') {
      const msgLower = message.toLowerCase();
      // 英文配额错误
      if (
        msgLower.includes('insufficient_quota') ||
        msgLower.includes('quota failed') ||
        (msgLower.includes('user quota') && msgLower.includes('need quota'))
      ) {
        return true;
      }
      // 中文配额/余额不足错误
      if (
        message.includes('配额不足') ||
        message.includes('余额不足') ||
        message.includes('额度不足') ||
        message.includes('账户余额') ||
        message.includes('账号余额')
      ) {
        return true;
      }
    }

    // 递归检查嵌套对象
    const nestedKeys = ['error', 'body', 'data', 'response', 'details'] as const;
    for (const key of nestedKeys) {
      if (key in obj && searchInObject(obj[key], depth + 1)) {
        return true;
      }
    }

    return false;
  };

  return searchInObject(error);
};

/**
 * 从错误消息中提取额度详情
 * 解析模式：'user quota: $X.XX, need quota: $Y.YY'
 */
export const extractQuotaDetails = (error: unknown): QuotaDetails => {
  const result: QuotaDetails = {};
  if (error == null) return result;

  // 提取消息文本
  const message = extractErrorMessage(error);
  if (!message) return result;

  // 匹配 'user quota: $0.200000' 或 'user quota: ＄0.200000'（全角美元符号）
  const userQuotaMatch = message.match(/user\s*quota[:\s]*[\$＄]?([\d.]+)/i);
  if (userQuotaMatch) {
    const parsed = parseFloat(userQuotaMatch[1]);
    if (!isNaN(parsed)) {
      result.userQuota = parsed;
    }
  }

  // 匹配 'need quota: $0.318000' 或 'need quota: ＄0.318000'
  const needQuotaMatch = message.match(/need\s*quota[:\s]*[\$＄]?([\d.]+)/i);
  if (needQuotaMatch) {
    const parsed = parseFloat(needQuotaMatch[1]);
    if (!isNaN(parsed)) {
      result.needQuota = parsed;
    }
  }

  return result;
};

/**
 * 统一错误分类函数
 * 根据错误类型返回结构化的分类结果，便于 UI 层做差异化处理
 */
export const classifyError = (error: unknown): ErrorClassification => {
  const rawMessage = extractErrorMessage(error) ?? 'Unknown error';

  // 1. 检测额度不足
  if (isQuotaInsufficientError(error)) {
    const { userQuota, needQuota } = extractQuotaDetails(error);
    return {
      type: 'quota_insufficient',
      userQuota: userQuota ?? 0,
      needQuota: needQuota ?? 0,
      rawMessage,
    };
  }

  // 2. 检测速率限制 (429)
  if (isRecord(error)) {
    const status = (error as { status?: number }).status;
    const body = error.body;
    const bodyStr = typeof body === 'string' ? body : JSON.stringify(body ?? '');

    if (
      status === 429 ||
      bodyStr.includes('RESOURCE_EXHAUSTED') ||
      bodyStr.includes('rate_limit') ||
      rawMessage.toLowerCase().includes('rate limit')
    ) {
      return { type: 'rate_limited', rawMessage };
    }

    // 3. 检测未授权
    if (
      status === 401 ||
      status === 403 ||
      rawMessage.toLowerCase().includes('unauthorized') ||
      rawMessage.toLowerCase().includes('forbidden')
    ) {
      return { type: 'unauthorized', rawMessage };
    }

    // 4. 检测服务器错误
    if (typeof status === 'number' && status >= 500) {
      return { type: 'server_error', status, rawMessage };
    }
  }

  // 5. 检测网络错误
  if (
    rawMessage.toLowerCase().includes('network') ||
    rawMessage.toLowerCase().includes('timeout') ||
    rawMessage.toLowerCase().includes('fetch')
  ) {
    return { type: 'network_error', rawMessage };
  }

  // 6. 未知错误
  return { type: 'unknown', rawMessage };
};

// ============================================================================
// 原有错误提取逻辑
// ============================================================================

const ERROR_FIELD_KEYS = [
  'error',
  'message',
  'msg',
  'detail',
  'description',
  'error_message',
  'errorMessage',
  'error_msg',
  'errorDescription',
  'reason',
  'cause',
] as const;

const ERROR_CONTAINER_KEYS = [
  'body',
  'data',
  'response',
  'details',
  'errorDetails',
  'error_details',
  'errorInfo',
  'info',
  'errors',
] as const;

const extractMessageFromUnknown = (value: unknown, visited: Set<unknown>): string | null => {
  if (value == null) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const firstChar = trimmed.charAt(0);
    const lastChar = trimmed.charAt(trimmed.length - 1);
    if (
      (firstChar === '{' && lastChar === '}') ||
      (firstChar === '[' && lastChar === ']')
    ) {
      try {
        const parsed = JSON.parse(trimmed);
        const nested = extractMessageFromUnknown(parsed, visited);
        if (nested) {
          return nested;
        }
      } catch {
        // 忽略 JSON 解析错误，返回原始字符串
      }
    }
    return trimmed;
  }

  if (value instanceof Error) {
    const message = typeof value.message === 'string' ? value.message.trim() : '';
    return message ? message : null;
  }

  if (!isRecord(value)) {
    return null;
  }

  if (visited.has(value)) {
    return null;
  }
  visited.add(value);

  if (Array.isArray(value)) {
    for (const item of value) {
      const nested = extractMessageFromUnknown(item, visited);
      if (nested) {
        return nested;
      }
    }
    return null;
  }

  const record = value as Record<string, unknown>;
  for (const key of ERROR_FIELD_KEYS) {
    if (key in record) {
      const nested = extractMessageFromUnknown(record[key], visited);
      if (nested) {
        return nested;
      }
    }
  }

  for (const key of ERROR_CONTAINER_KEYS) {
    if (key in record) {
      const nested = extractMessageFromUnknown(record[key], visited);
      if (nested) {
        return nested;
      }
    }
  }

  return null;
};

/**
 * 尽可能从未知错误对象中提取用户可读的信息。
 */
export const extractErrorMessage = (error: unknown): string | null => {
  if (error == null) {
    return null;
  }

  const visited = new Set<unknown>();
  if (isRecord(error)) {
    for (const key of ['body', 'data', 'response'] as const) {
      if (key in error) {
        const nested = extractMessageFromUnknown((error as Record<string, unknown>)[key], visited);
        if (nested) {
          return nested;
        }
      }
    }
  }

  return extractMessageFromUnknown(error, visited);
};

const parseErrorPayload = (value: unknown): unknown => {
  if (value == null) {
    return undefined;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }
    const firstChar = trimmed.charAt(0);
    const lastChar = trimmed.charAt(trimmed.length - 1);
    if (
      (firstChar === '{' && lastChar === '}') ||
      (firstChar === '[' && lastChar === ']')
    ) {
      try {
        return JSON.parse(trimmed);
      } catch {
        // 忽略解析失败，回退为原始字符串
      }
    }
    return trimmed;
  }
  return value;
};

/**
 * 尽量提取错误对象中的原始响应内容，便于历史记录持久化。
 */
export const extractErrorPayload = (error: unknown): unknown => {
  if (error == null) {
    return undefined;
  }

  if (isRecord(error)) {
    const directKeys = ['body', 'data', 'raw', 'payload'] as const;
    for (const key of directKeys) {
      if (key in error) {
        const candidate = parseErrorPayload((error as Record<string, unknown>)[key]);
        if (candidate !== undefined) {
          return candidate;
        }
      }
    }

    if ('response' in error) {
      const responseVal = (error as Record<string, unknown>).response;
      if (isRecord(responseVal)) {
        const nestedKeys = ['data', 'body', 'raw'] as const;
        for (const key of nestedKeys) {
          if (key in responseVal) {
            const nestedCandidate = parseErrorPayload((responseVal as Record<string, unknown>)[key]);
            if (nestedCandidate !== undefined) {
              return nestedCandidate;
            }
          }
        }
      }
      const normalizedResponse = parseErrorPayload(responseVal);
      if (normalizedResponse !== undefined) {
        return normalizedResponse;
      }
    }
  }

  return parseErrorPayload(error);
};

/**
 * 记录网络错误详情，方便调试定位。
 */
export const logNetworkError = (detail: string | null, fallback: unknown) => {
  if (detail && detail.trim()) {
    console.warn('[generation] network error:', detail);
    return;
  }
  if (fallback instanceof Error && typeof fallback.message === 'string') {
    console.warn('[generation] network error:', fallback.message, fallback);
    return;
  }
  console.warn('[generation] network error:', fallback);
};
